import { ChangeEvent, KeyboardEvent, RefObject, useEffect, useRef, useState } from "react";

import { AgentChatAttachment } from "../types";

import { useAutoGrowTextarea } from "./useAutoGrowTextarea";
import { useControllableState } from "./useControllableState";

/* Shown when the user tries to send another message while a reply is still in flight. */
const LOADING_SUBMIT_NOTICE = "Please wait for the current response to finish before sending another message.";

interface ChatInputConfig {
  readonly inputValue?: string;
  readonly onInputChange?: (value: string) => void;
  readonly onSubmit?: (submission: { content: string; attachments: readonly AgentChatAttachment[] }) => void;
  readonly isLoading: boolean;
  readonly attachedFiles: readonly AgentChatAttachment[];
}

interface ChatInputResult {
  readonly textareaRef: RefObject<HTMLTextAreaElement>;
  readonly currentInputValue: string;
  readonly canSubmit: boolean;
  readonly loadingNotice: string | undefined;
  readonly populateInput: (value: string) => void;
  readonly handleTextareaChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  readonly handleTextareaKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  readonly handleSubmit: () => void;
}

/**
 * Manages the message input, supporting either a controlled `inputValue` or its own internal
 * state. Blocks submits while empty or loading and surfaces a notice when a send is attempted
 * mid-response. Self-managed input is cleared after a successful send.
 */
export const useChatInput = ({ inputValue, onInputChange, onSubmit, isLoading, attachedFiles }: ChatInputConfig): ChatInputResult => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [currentInputValue, setInternalInputValue, isControlled] = useControllableState(inputValue, "");
  const [showLoadingNotice, setShowLoadingNotice] = useState(false);

  const trimmedInputValue = currentInputValue.trim();
  const canSubmit = trimmedInputValue.length > 0 || attachedFiles.length > 0;

  useAutoGrowTextarea(textareaRef, currentInputValue);

  useEffect(() => {
    if (!isLoading) setShowLoadingNotice(false);
  }, [isLoading]);

  const populateInput = (value: string) => {
    if (!isControlled) setInternalInputValue(value);
    onInputChange?.(value);
    textareaRef.current?.focus();
  };

  const handleTextareaChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (!isControlled) setInternalInputValue(e.target.value);
    onInputChange?.(e.target.value);
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (isLoading) {
      setShowLoadingNotice(true);
      return;
    }
    setShowLoadingNotice(false);
    onSubmit?.({ content: trimmedInputValue, attachments: attachedFiles });
    /* Clear our own input after sending; controlled callers clear it via props. */
    if (!isControlled) setInternalInputValue("");
  };

  const handleTextareaKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return {
    textareaRef,
    currentInputValue,
    canSubmit,
    loadingNotice: showLoadingNotice ? LOADING_SUBMIT_NOTICE : undefined,
    populateInput,
    handleTextareaChange,
    handleTextareaKeyDown,
    handleSubmit,
  };
};
